import type { ReactNode } from "react";
import { Search as SearchIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export interface ChannelTableColumn<T> {
  key: string;
  label: string;
  /** Piso da coluna, ex. "minmax(5rem,2fr)" ou "2.5rem" — nunca fr puro
   * (mesma regra do DataTable compartilhado, components/ui/DataTable.tsx). */
  width: string;
  mono?: boolean;
  /** Alinhamento do texto — protótipo alinha SYNC à direita. */
  align?: "left" | "right";
  render: (row: T) => ReactNode;
}

export interface ChannelProductsCardProps<T> {
  columns: ChannelTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  renderCard: (row: T) => ReactNode;
  emptyState: ReactNode;
  isEmpty: boolean;
  search: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    ariaLabel: string;
  };
  pagination: {
    label: string;
    hasPrev: boolean;
    hasNext: boolean;
    onPrev: () => void;
    onNext: () => void;
  } | null;
}

/**
 * Cartão único do Canal (brief fidelidade-3: "tabela num cartão único...
 * sem cartão separado de busca ... paginação dentro do cartão"). Não usa o
 * DataTable compartilhado (components/ui/DataTable.tsx, fora do escopo
 * desta frente — proibido editar) porque ele não aceita busca nem rodapé;
 * em vez disso reaproveita as MESMAS classes CSS globais que o DataTable já
 * usa (`.row`, `.row__head`, `.row__cell`, `.card`) — nenhuma classe nova,
 * nenhuma edição em index.css.
 */
export function ChannelProductsCard<T>({
  columns,
  rows,
  rowKey,
  renderCard,
  emptyState,
  isEmpty,
  search,
  pagination,
}: ChannelProductsCardProps<T>): JSX.Element {
  const template = columns.map((c) => c.width).join(" ");

  return (
    <div className="card" style={{ padding: 0 }}>
      {/* Busca compacta no topo do cartão (brief: "busca compacta no topo da
          tabela ... sem cartão separado de busca"). */}
      <div style={{ padding: "0.75rem 1rem", borderBottom: "0.0625rem solid var(--color-line)" }}>
        <div className="relative">
          <SearchIcon
            className="icon"
            aria-hidden
            style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)" }}
          />
          <Input
            type="search"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder}
            style={{ paddingLeft: "2.25rem" }}
            aria-label={search.ariaLabel}
          />
        </div>
      </div>

      {isEmpty ? (
        <div style={{ padding: "1rem" }}>{emptyState}</div>
      ) : (
        <>
          {/* Desktop: grade com piso em cada coluna (mesmo padrão do DataTable) */}
          <div className="hidden md:block">
            <div className="row row__head" style={{ gridTemplateColumns: template, padding: "0 1rem" }}>
              {columns.map((c) => (
                <span key={c.key} className="row__cell" style={c.align === "right" ? { textAlign: "right" } : undefined}>
                  {c.label}
                </span>
              ))}
            </div>
            {rows.map((row) => (
              <div key={rowKey(row)} className="row" style={{ gridTemplateColumns: template, padding: "0 1rem" }}>
                {columns.map((c) => (
                  <span
                    key={c.key}
                    className={cn("row__cell", c.mono && "mono")}
                    style={c.align === "right" ? { textAlign: "right" } : undefined}
                  >
                    {c.render(row)}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {/* Mobile: lista de cartões — wrapper isolado do Tailwind `md:hidden`,
              nunca combinado com `.stack` no mesmo elemento (achado DataTable:
              `.stack{display:flex}` vence o `display` responsivo do Tailwind
              nesta versão do build, reproduzido em /admin/tags e /admin/usuarios). */}
          <div className="md:hidden">
            <div className="stack" style={{ padding: ".75rem" }}>
              {rows.map((row) => (
                <div key={rowKey(row)}>{renderCard(row)}</div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Paginação DENTRO do cartão (brief: "Página N de M — X no total ·
          Anterior · Próxima" dentro do cartão, não como irmão embaixo). */}
      {!isEmpty && pagination && (pagination.hasPrev || pagination.hasNext) ? (
        <div
          className="spread"
          style={{ padding: "0.75rem 1rem", borderTop: "0.0625rem solid var(--color-line)" }}
        >
          <span style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>{pagination.label}</span>
          <span className="cluster">
            {pagination.hasPrev ? (
              <Button variant="link" size="sm" onClick={pagination.onPrev}>
                Anterior
              </Button>
            ) : null}
            {pagination.hasPrev && pagination.hasNext ? (
              <span style={{ color: "var(--color-ink-3)" }} aria-hidden>
                ·
              </span>
            ) : null}
            {pagination.hasNext ? (
              <Button variant="link" size="sm" onClick={pagination.onNext}>
                Próxima
              </Button>
            ) : null}
          </span>
        </div>
      ) : null}
    </div>
  );
}
